import { ReactElement, ReactNode } from 'react';
import Button from '../Button';

interface ActionGuardProps {
  open: boolean;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  actionFn: () => void | Promise<void>;
  onCancel?: () => void;
  onClose: () => void;
}

export default function ActionGuard({
  open,
  message,
  confirmLabel = 'Yes',
  cancelLabel = 'No',
  actionFn,
  onCancel,
  onClose,
}: ActionGuardProps): ReactElement | null {
  const handleConfirm = () => {
    actionFn();
    onClose();
  };

  const handleCancel = () => {
    onCancel?.();
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!open) {
    return null;
  }

  return (
    <div
      className="cr-fixed cr-inset-0 cr-z-[9999] cr-flex cr-items-center cr-justify-center cr-bg-black cr-bg-opacity-50"
      onClick={handleBackdropClick}
      data-testid="action-guard"
      aria-labelledby="Destructive action warning"
      aria-describedby="Do you want to perform action which leads to lost data?"
    >
      <div className="cr-mx-4 cr-max-w-md cr-rounded-lg cr-bg-white cr-p-6 cr-shadow-xl">
        <div className="cr-mb-4 cr-text-sm cr-text-gray-700" style={{ whiteSpace: 'break-spaces' }}>
          {message}
        </div>
        <div className="cr-flex cr-justify-end cr-gap-2">
          <Button onClick={handleCancel} data-testid="action-guard-no-btn">
            {cancelLabel}
          </Button>
          <Button onClick={handleConfirm} data-testid="action-guard-yes-btn">
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
